import { FormSectionHandler } from '../entity.handler';
import { DeviceResponse, DeviceUpdateRequest, IdName, DeviceTypeListItem } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, IdNameComboValueHandlers, EMPTY_GUID } from '../utils';
import { KeyFormBuilderComboFieldComponent } from 'app/shared/components/form-builder/combo/combo.component';

interface EditorValues {
    name?: string;
    deviceType?: IdName;
    configProfile?: IdName;
    provider?: IdName;

}

export class DeviceDetailsSectionHandler implements FormSectionHandler<DeviceResponse, EditorValues> {

    type = 'form';
    icon = 'microchip';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['name', 'configProfile', 'deviceType', 'provider'];

    static getAvailableRootDeviceTypeIdsForClient(admin: AdminEditorService, clientId: string): Promise<string[]> {
        return admin.getCached(`getClient/${clientId}`, api => api.accounts.getClient(clientId)).then(async client => {
            let available = (client.availableDeviceTypes || []).map(x => x.id);
            if (available.length === 0) { // this means ALL, we need to go up to the vendor to find our next set of limitations
                const vendor = await admin.getCached(`getVendor/${client.owner.id}`, api => api.accounts.getVendor(client.owner.id));
                available = (vendor.availableDeviceTypes || []).map(x => x.id);
            }
            return available;
        });
    }

    static async getAvailableRootDeviceTypeIdsForVendor(admin: AdminEditorService, vendorId: string): Promise<string[]> {
        const vendor = await admin.getCached(`getVendor/${vendorId}`, api => api.accounts.getVendor(vendorId));
        return (vendor.availableDeviceTypes || []).map(x => x.id);
    }

    static filterAllowedDeviceTypes(ownerId: string, deviceTypes: DeviceTypeListItem[], allowedTypes: string[], exceptions: string[]): { key: string, value: string }[] {
        return deviceTypes
            .filter(item => exceptions.includes(item.id) || item.state === 'active' && item.parent && (item.owner.id === ownerId || (allowedTypes.length === 0 || allowedTypes.includes(item.parent.id))))
            .map(item => ({
                key: item.id,
                value: deviceTypes.find(x => x.id === item.parent.id).name + ' - ' + item.name,
            }))
            .sort((a, b) => a.value.localeCompare(b.value));
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: DeviceResponse, payload: DeviceUpdateRequest): Promise<DeviceResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDevice(entity.id, payload), { _type: 'device', _reload: true, _cacheKey: `getDevice/${entity.id}` });
    }

    async mapEntityToEditor(entity: DeviceResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        if (!result.configProfile) {
            result.configProfile = { id: EMPTY_GUID, name: this.translate('FIELDS.MANUAL_CONFIG') };
        }
        return result;
    }

    mapEditorToEntity(entity: DeviceUpdateRequest, values: EditorValues): Promise<DeviceUpdateRequest> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    getFields(entity: DeviceResponse): Promise<FormBuilderField[]> {

        return Promise.all([
            DeviceDetailsSectionHandler.getAvailableRootDeviceTypeIdsForClient(this.admin, this.ownerId),
            this.admin.getCached(`listDeviceTypes/${this.ownerId}`, api => api.entities.listDeviceTypes(this.ownerId)),
            this.admin.getCached(`listDeviceProviders/${this.ownerId}/true`, api => api.entities.listDeviceProviders(this.ownerId, true)),
            this.admin.getCached(`listDeviceConfigProfiles/${this.ownerId}/true`, api => api.entities.listDeviceConfigProfiles(this.ownerId, true)),
        ]).then(async ([available, deviceTypes, deviceProviders, configProfiles]) => {

            let currentDeviceTypeId = entity && entity.deviceType && entity.deviceType.id;
            const currentConfigProfileId = entity && entity.configProfile && entity.configProfile.id;
            const deviceTypeValues = DeviceDetailsSectionHandler.filterAllowedDeviceTypes(this.ownerId, deviceTypes.items, available, [currentDeviceTypeId]) as FormBuilderKeyValue[];

            // bit of a mess, but avoids a large refactor
            deviceTypeValues.forEach(item => {
                const deviceType = deviceTypes.items.find(x => x.id === item.key);
                if (deviceType && deviceType.deviceProviderType) {
                    const providers = deviceProviders.items.filter(x => x.deviceProviderType.id === deviceType.deviceProviderType.id && x.state === 'active');
                    if (providers) {
                        item.fields = [{
                            ...ComboField('provider', this.translate('FIELDS.PROVIDER'), true, providers.map(x => ({ key: x.id, value: x.name })), IdNameComboValueHandlers),
                            getHref: (field: FormBuilderField, values: any) => {
                                const val = values[field.id];
                                return val ? `/admin/client/${entity.owner.id}/deviceproviders/grid/deviceprovider/${val.id}` : null;
                            },
                        } as any];
                    }
                }
            });

            const getConfigProfiles = (deviceType: string) => {
                return [
                    { key: EMPTY_GUID, value: this.translate('FIELDS.MANUAL_CONFIG') },
                    ...configProfiles.items.filter(x => {
                        return (x.state === 'active' || x.id === currentConfigProfileId) && x.deviceType.id === deviceType;
                    }).map(x => ({ key: x.id, value: x.name })),
                ]
            };

            const configField = ComboField('configProfile', this.translate('FIELDS.CONFIG_PROFILE'), true, getConfigProfiles(currentDeviceTypeId), IdNameComboValueHandlers);
            return [
                TextField('name', this.translate('FIELDS.SERIAL_NUMBER'), true, 1, 255),
                {
                    ...ComboField('deviceType', this.translate('FIELDS.DEVICE_TYPE'), true, deviceTypeValues, IdNameComboValueHandlers),
                    setValue: async (field: FormBuilderField, values: any, value: any) => {

                        values[field.id] = { id: value };

                        if (currentDeviceTypeId !== value) {
                            if (configField.component instanceof KeyFormBuilderComboFieldComponent) {
                                configField.values = getConfigProfiles(value);
                                configField.component.comboValues = configField.values;
                                configField.component.comboChanged(EMPTY_GUID);
                            }
                        }
                        currentDeviceTypeId = value;
                    },
                },
                configField,
                !entity && deviceProviders.items.length > 0 ? ComboField('provider', this.translate('FIELDS.PROVIDER'), true, deviceProviders.items.map(x => ({ key: x.id, value: x.name })), IdNameComboValueHandlers) : null,
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, ['deviceType', 'configProfile', 'provider'], fields));
    }

}
